import { ReactNode } from 'react';

import { DownloadIcon, PaperclipIcon } from 'lucide-react';
import { useSelector } from 'react-redux';

import { cn } from '@/lib/utils';
import { Attachment } from '@/modules/humanChat/components/Conversation/MessageArea/Attachment/Attachment';
import { buildStoredFileUrl } from '@/modules/humanChat/hooks/useAttachment';
import { selectSessionSource } from '@/modules/tryIt/redux/selectors';
import { AuthorType } from '@/shared/types/chat';
import { ResponseMessage } from '@/shared/types/tryIt';

import { bubbleBrainClasses, responseClasses } from './constants';

type FileMessage = Extract<ResponseMessage, { type: 'file' }>;

interface FileResponseProps {
  response: FileMessage;
  authorType: `${AuthorType}`;
  icon: ReactNode;
  goToButton: ReactNode;
  turnRow?: boolean;
}

export const FileResponse = ({
  response,
  authorType,
  icon,
  goToButton,
  turnRow = false,
}: FileResponseProps) => {
  const source = useSelector(selectSessionSource);
  const fileUrl = buildStoredFileUrl(source, response.url, response.name);

  return (
    <div className={responseClasses}>
      {icon}
      {/* `bubbleBrainClasses` paints links informational, which outranks the link's own colour. */}
      <p className={cn(turnRow ? 'w-full min-w-0 break-words' : bubbleBrainClasses)}>
        {response.id ? (
          <Attachment
            source={source}
            attachment={{ id: response.id, type: 'file', mime_type: '' }}
            authorType={authorType}
          />
        ) : (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'flex items-center',
              turnRow
                ? 'w-fit min-w-16 justify-center gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-sm leading-6 font-medium text-foreground transition hover:bg-highlight-background'
                : 'text-informational'
            )}
          >
            {turnRow ? (
              <DownloadIcon size={16} className="shrink-0 text-foreground-muted" />
            ) : (
              <PaperclipIcon className="mr-1" />
            )}
            {response.name}
          </a>
        )}
      </p>
      {goToButton}
    </div>
  );
};
